/**
 * dispatch/funnel-model — pure fold of the dispatch endpoints into the
 * "today's dispatch picture" funnel view model. ALL threshold/status
 * logic lives here (spec 2026-07-24-dispatch-funnel-panel-design §4);
 * components render, they never decide. Onfleet-coupled levels land here
 * too (transitional — swap in ONE place when Onfleet is retired).
 */
import type { components } from '@/types/api.generated';

type SalesBridge = components['schemas']['SalesBridge'];
type DispatchPreview = components['schemas']['DispatchPreview'];
type RoutePlanningStops = components['schemas']['RoutePlanningStops'];
type MissingReason = RoutePlanningStops['missing'][number]['reason'];

export type FunnelStatus = 'neutral' | 'ok' | 'alert';

export interface DispatchFunnelViewModel {
  demand: {
    orders: number;
    units: number;
    status: FunnelStatus;
    sameProductMultiQtyOrders: number;
    excludedOrders: number;
    excludedUnits: number;
  };
  jobs: {
    jobs: number;
    gap: number;
    gapOrders: Array<{ orderName: string; units: number; orderId: string }>;
    status: FunnelStatus;
  };
  dispatch: {
    dispatched: number;
    undispatched: number;
    pastCutoff: boolean;
    /** Tenant dispatch cutoff, HH:mm wall clock, from the preview. */
    cutoffTime: string;
    /**
     * The jobs still waiting on an Onfleet task. Dispatch acts per JOB, so the
     * drill-down carries job identity (#1675); `orderName` stays only as the
     * human context an operator reads the label by.
     */
    undispatchedJobs: Array<{
      jobId: string;
      jobName: string;
      orderName: string;
    }>;
    status: FunnelStatus;
  };
  coordinates: {
    ready: number;
    pending: number;
    alerts: Array<{
      orderName: string;
      jobId: string;
      orderId?: string;
      reason: MissingReason;
      dispatched: boolean;
    }>;
    status: FunnelStatus;
  };
}

export function buildFunnelModel(inputs: {
  bridge: SalesBridge;
  preview: DispatchPreview;
  stops: RoutePlanningStops;
}): DispatchFunnelViewModel {
  const { bridge } = inputs;
  const missingUnits = bridge.missing_jobs.reduce((sum, entry) => sum + entry.units, 0);
  const gap = bridge.missing_jobs.length;
  const undispatched = bridge.undispatched.map((entry) => ({
    jobId: entry.job_id,
    jobName: entry.job_name,
    orderName: entry.order_name,
  }));
  const dispatch: DispatchFunnelViewModel['dispatch'] = {
    dispatched: bridge.totals.jobs_planned - undispatched.length,
    undispatched: undispatched.length,
    pastCutoff: inputs.preview.past_dispatch_cutoff,
    cutoffTime: inputs.preview.dispatch_cutoff_time,
    undispatchedJobs: undispatched,
    status: (undispatched.length === 0
      ? 'ok'
      : inputs.preview.past_dispatch_cutoff
        ? 'alert'
        : 'neutral') satisfies FunnelStatus,
  };
  const coordinateAlerts = inputs.stops.missing
    .filter((entry) => entry.reason === 'order_not_found' || entry.dispatched)
    .map((entry) => ({
      orderName: entry.order_name,
      jobId: entry.job_id,
      orderId: entry.order_id,
      reason: entry.reason,
      dispatched: entry.dispatched,
    }));
  const coordinatePending = inputs.stops.missing.filter(
    (entry) => !entry.dispatched && entry.reason !== 'order_not_found',
  ).length;
  const coordinates: DispatchFunnelViewModel['coordinates'] = {
    ready: inputs.stops.stops.length,
    pending: coordinatePending,
    alerts: coordinateAlerts,
    status: coordinateAlerts.length > 0 ? 'alert' : coordinatePending > 0 ? 'neutral' : 'ok',
  };
  return {
    demand: {
      orders: bridge.totals.orders_live + gap,
      units: bridge.totals.units_live + missingUnits,
      status: 'neutral',
      sameProductMultiQtyOrders: new Set(bridge.multi_qty_detail.map((entry) => entry.order_name))
        .size,
      excludedOrders: bridge.exceptions.length,
      excludedUnits: bridge.totals.units_excluded,
    },
    jobs: {
      jobs: bridge.totals.jobs_planned,
      gap,
      gapOrders: bridge.missing_jobs.map((entry) => ({
        orderName: entry.order_name,
        units: entry.units,
        orderId: entry.order_id,
      })),
      status: gap > 0 ? 'alert' : 'ok',
    },
    dispatch,
    coordinates,
  };
}
